import {
  AiDocumentReviewAllowedActions,
  AiDocumentReviewMode,
} from 'src/constants/ai-document-review/enums'
import {
  AI_REVIEW_ANY_LABEL_TEXT,
  AI_REVIEW_DEFAULT_AUTO_APPROVE_MIN_SLP_RATE,
} from 'src/constants/ai-review/constants'
import type { AiDocumentReviewConfig } from 'src/interfaces/aiDocumentReviewConfig'
import type { AiReviewModeLabel } from './aiReviewConfigFormatters.types'

/**
 * Every boolean on a config is optional, and the backend treats an absent one as ON. So these
 * read `!== false` rather than truthiness — `undefined` means the check runs.
 */
export const formatChecks = (config: AiDocumentReviewConfig): string => {
  const checks: string[] = []

  if (config.identityMatchRequired !== false) {
    checks.push('Identity')
  }

  if (config.vehicleMatchRequired !== false) {
    checks.push('Vehicle')
  }

  if (config.expirationCheckRequired !== false) {
    checks.push('Expiration')
  }

  if (checks.length === 0) {
    return 'None'
  }

  return checks.join(' · ')
}

const hasExplicitThreshold = (config: AiDocumentReviewConfig): boolean => {
  return config.autoApproveMinSlpRate !== undefined && config.autoApproveMinSlpRate !== null
}

export const formatThreshold = (config: AiDocumentReviewConfig): string => {
  if (!hasExplicitThreshold(config)) {
    return `Default (${AI_REVIEW_DEFAULT_AUTO_APPROVE_MIN_SLP_RATE})`
  }

  return String(config.autoApproveMinSlpRate)
}

export const resolveAllowedActions = (
  config: AiDocumentReviewConfig,
): AiDocumentReviewAllowedActions => {
  return config.allowedActions ?? AiDocumentReviewAllowedActions.All
}

export const resolveModeLabel = (config: AiDocumentReviewConfig): AiReviewModeLabel => {
  if (config.enabled === false) {
    return 'Review off'
  }

  if (config.mode === AiDocumentReviewMode.Active) {
    return 'Active'
  }

  return 'Shadow'
}

/**
 * Active mode that is allowed to approve, with no explicit threshold, falls back to the backend
 * default — worth flagging in the list because it is the one combination that can auto-approve
 * on a number nobody chose. Shadow applies nothing, so it never warns.
 */
export const hasMissingThresholdWarning = (config: AiDocumentReviewConfig): boolean => {
  if (config.enabled === false || config.mode !== AiDocumentReviewMode.Active) {
    return false
  }

  const allowedActions = resolveAllowedActions(config)
  const canApprove =
    allowedActions === AiDocumentReviewAllowedActions.All ||
    allowedActions === AiDocumentReviewAllowedActions.ApproveOnly

  return canApprove && !hasExplicitThreshold(config)
}

export const formatStepLabel = (config: AiDocumentReviewConfig): string => {
  const label = config.label?.trim()

  if (!label) {
    return AI_REVIEW_ANY_LABEL_TEXT
  }

  return label
}

export const formatInstructions = (config: AiDocumentReviewConfig): string => {
  const instructions = config.instructions?.trim()

  if (!instructions) {
    // A dash for a missing optional value is a cosmetic default, not fabricated content.
    return '—'
  }

  return 'Included'
}
